import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from 'next-themes';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { PageContextProvider } from '@/contexts/PageContext';
import { useAuth } from '@/contexts/SharedAuthContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { trpc, trpcClient } from '@/lib/trpc';

// Pages
import { LoginPage } from '@/pages/LoginPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';
import BrainMeetPrivacyPage from '@/pages/BrainMeetPrivacyPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { SelectTicketTypePage } from '@/pages/tickets/SelectTicketTypePage';
import { NewTicketPage } from '@/pages/tickets/NewTicketPage';
import { MyTicketsPage } from '@/pages/tickets/MyTicketsPage';
import { AllTicketsPage } from '@/pages/admin/AllTicketsPage';
import { InventoryPage } from '@/pages/admin/InventoryPage';
import { UserManagementPage } from '@/pages/admin/UserManagementPage';
import { ApprovalForPurchasePage } from '@/pages/admin/ApprovalForPurchasePage';
import { UsefulInfoPage } from '@/pages/admin/UsefulInfoPage';
import { PeoplePanelPage } from '@/pages/admin/people-panel/PeoplePanelPage';
import { UserRequestsKanbanPage } from '@/pages/admin/UserRequestsKanbanPage';
import { InOutPage } from '@/pages/admin/InOutPage';
import { CommunityMapPage } from '@/pages/admin/CommunityMapPage';
import { OfficesPage } from '@/pages/admin/OfficesPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { SubscriptionsIntelligencePage } from '@/pages/ia-native/SubscriptionsIntelligencePage';
import { ObservabilityPage } from '@/pages/ia-native/ObservabilityPage';
import { FinOpsPage } from '@/pages/technology/FinOpsPage';
import { FinOpsAlertsPage } from '@/pages/technology/FinOpsAlertsPage';
import { FINOPS_ALERTS_SUBTAB } from '@/components/finops/alerts-format';
import { UserPermissionsPage } from '@/pages/admin/UserPermissionsPage';
import { InternalPanelPage } from '@/pages/internal/InternalPanelPage';
import { PageViewBeacon } from '@/components/internal/PageViewBeacon';
import { OfficeManagerPage } from '@/pages/admin/OfficeManagerPage';
import { OfficeManagerProtectedRoute } from '@/components/office-manager/OfficeManagerProtectedRoute';
import { SkillsAgentsPage } from '@/pages/SkillsAgentsPage';
import { NewSkillAgentPage } from '@/pages/NewSkillAgentPage';
import { SkillVersionHistoryPage } from '@/pages/SkillVersionHistoryPage';
import { PerformanceEvaluation } from '@/pages/performance/PerformanceEvaluation';
import { PerformanceAnalysis } from '@/pages/performance/PerformanceAnalysis';
import { PerformanceFeedbackResult } from '@/pages/performance/PerformanceFeedbackResult';
import { PerformanceReview } from '@/pages/performance/PerformanceReview';
import { PerformanceHistory } from '@/pages/performance/PerformanceHistory';
import { PerformanceClapperProfile } from '@/pages/performance/PerformanceClapperProfile';
import { PerformanceAdminLayout } from '@/pages/performance/PerformanceAdminLayout';
import { PerformanceAdminDashboard } from '@/pages/performance/PerformanceAdminDashboard';
import { PerformanceAdminEpics } from '@/pages/performance/PerformanceAdminEpics';
import { PerformanceAdminAssistants } from '@/pages/performance/PerformanceAdminAssistants';
import { PerformanceAdminKnowledge } from '@/pages/performance/PerformanceAdminKnowledge';
import { PerformanceAdminNotifications } from '@/pages/performance/PerformanceAdminNotifications';
import { TalentMapGovernancePage } from '@/pages/performance/TalentMapGovernancePage';
import { TalentMapProtectedRoute } from '@/components/performance/TalentMapProtectedRoute';
import { TalentMapLayout, LegacyTalentMapRedirect } from '@/pages/performance/talent-map/TalentMapLayout';
import { TalentMapTeamTab } from '@/pages/performance/talent-map/TalentMapTeamTab';
import { TalentMapEvaluationTab } from '@/pages/performance/talent-map/TalentMapEvaluationTab';
import { TalentMapHistoryTab } from '@/pages/performance/talent-map/TalentMapHistoryTab';
import { TalentMapAnalyticsTab } from '@/pages/performance/talent-map/TalentMapAnalyticsTab';
import { PerformanceDTalk } from '@/pages/performance/PerformanceDTalk';
import { DTalkLayout } from '@/pages/performance/dtalk/DTalkLayout';
import { DTalkPeopleTab } from '@/pages/performance/dtalk/DTalkPeopleTab';
import { DTalkDetail } from '@/pages/performance/dtalk/DTalkDetail';
import { DTalkHistoryTab } from '@/pages/performance/dtalk/DTalkHistoryTab';
import PricingSavedVersionsPage from '@/pages/finance/pricing/PricingSavedVersionsPage';
import PricingEditorPage from '@/pages/finance/pricing/PricingEditorPage';
import PricingTutorialPage from '@/pages/finance/pricing/PricingTutorialPage';
import { BudgetControlPage } from '@/pages/finance/budget-control/BudgetControlPage';
import { FinancePnlProtectedRoute } from '@/components/finance/budget-control/FinancePnlProtectedRoute';
import { BrainChatPage } from '@/pages/brain/BrainChatPage';
import { PeopleLandingPage } from '@/pages/people-landing/PeopleLandingPage';
import { LearningPage } from '@/pages/people-learning/LearningPage';
import { ArcadePage } from '@/pages/people-learning/ArcadePage';
import { BrainClientsPage } from '@/pages/brain/BrainClientsPage';
import { PartnershipsOverviewPage } from '@/pages/partnerships/PartnershipsOverviewPage';
import { PartnerHubPage } from '@/pages/partnerships/PartnerHubPage';
import { PartnerProductsPage } from '@/pages/partnerships/PartnerProductsPage';
import { PartnerPackagesPage } from '@/pages/partnerships/PartnerPackagesPage';
import { PartnerCasesPage } from '@/pages/partnerships/PartnerCasesPage';
import { PartnerFAQPage } from '@/pages/partnerships/PartnerFAQPage';
import { PartnershipsGovernancePage } from '@/pages/partnerships/PartnershipsGovernancePage';
import { PartnershipsProtectedRoute } from '@/components/partnerships/PartnershipsProtectedRoute';
import { OmlProtectedRoute } from '@/components/owned-media/OmlProtectedRoute';
import { OmlAccountsPage } from '@/pages/owned-media/OmlAccountsPage';
import { OmlAccountDetailPage } from '@/pages/owned-media/OmlAccountDetailPage';
import { OmlTransversalPage } from '@/pages/owned-media/OmlTransversalPage';
import { OmlHealthPage } from '@/pages/owned-media/OmlHealthPage';
import { OmlArchivedPage } from '@/pages/owned-media/OmlArchivedPage';
import { OmlAdminPage } from '@/pages/owned-media/OmlAdminPage';
import { OmlHomePage } from '@/pages/owned-media/OmlHomePage';
import { OrgHomePage } from '@/pages/org/OrgHomePage';
import { OrgSolutionPage } from '@/pages/org/OrgSolutionPage';
import { OrgPersonPage } from '@/pages/org/OrgPersonPage';
import { OrgClientPage } from '@/pages/org/OrgClientPage';
import { OrgAdminPage } from '@/pages/org/OrgAdminPage';
import { OrgApiDocsPage } from '@/pages/org/OrgApiDocsPage';
import { StudioProtectedRoute } from '@/components/studio/StudioProtectedRoute';
import { StudioPage } from '@/pages/studio/StudioPage';
import { StudioTourPage } from '@/pages/studio/StudioTourPage';
import { TalentAcquisitionPage } from '@/pages/talent-acquisition/TalentAcquisitionPage';
import { TaIntakePage } from '@/pages/talent-acquisition/TaIntakePage';
import { TaInterviewPage } from '@/pages/talent-acquisition/TaInterviewPage';
import { TaProtectedRoute } from '@/components/talent-acquisition/TaProtectedRoute';
import { VaultProtectedRoute } from '@/components/vault/VaultProtectedRoute';
import { VaultPage } from '@/pages/vault/VaultPage';
import { SecretDetailPage } from '@/pages/vault/SecretDetailPage';
import { GroupsPage } from '@/pages/vault/GroupsPage';
import { ShareLinkPage } from '@/pages/vault/ShareLinkPage';
import { SharedAccountsPage } from '@/pages/vault/SharedAccountsPage';
import { CensusFormPage } from '@/pages/vault/CensusFormPage';
import { AuditPage } from '@/pages/vault/AuditPage';
import { WorkshopPage } from '@/pages/vault/WorkshopPage';
import { ProductDetailPage } from '@/pages/partnerships/ProductDetailPage';
import { PackageDetailPage } from '@/pages/partnerships/PackageDetailPage';
import { CaseDetailPage } from '@/pages/partnerships/CaseDetailPage';

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
          <PageContextProvider>
            {/* Telemetría de navegación del Panel de Internal (no renderiza nada) */}
            <PageViewBeacon />
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              {/* Vault F5 — link temporal: pública (sin login), estática; el
                  reveal es un POST con click explícito, nunca el GET. */}
              <Route path="/vault/s/:linkId" element={<ShareLinkPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/privacy/brain-meet" element={<BrainMeetPrivacyPage />} />

              {/* Protected Routes — any authenticated user */}
              <Route element={<ProtectedRoute authHook={useAuth} />}>
                {/* Clapper Arcade — inmersivo a pantalla completa, SIN DashboardLayout
                    (feedback Sol 01/09: nada de recuadros; el juego ES la pantalla) */}
                <Route path="/people/learning/arcade" element={<ArcadePage />} />
                {/* Product Tour del Studio — inmersivo como el Arcade; gate por la pieza `studio` del sidebar */}
                <Route element={<StudioProtectedRoute />}>
                  <Route path="/studio/product-tour" element={<StudioTourPage />} />
                </Route>
                <Route element={<DashboardLayout />}>
                  <Route path="/dashboard" element={<DashboardPage />} />

                  {/* Profile — all authenticated users */}
                  <Route path="/profile" element={<ProfilePage />} />

                  {/* Skills & Agentes — all authenticated users */}
                  <Route path="/skills-agentes" element={<SkillsAgentsPage />} />

                  {/* Winclap Brain — all authenticated users */}
                  <Route path="/brain" element={<BrainChatPage />} />
                  <Route path="/brain/c/:conversationId" element={<BrainChatPage />} />
                  <Route path="/brain/clients/new" element={<BrainClientsPage />} />

                  {/* Org Service (26/10/2026) — directorio de TODO clapper autenticado; el backend recorta
                      las aristas persona ↔ cliente por scope y /org/admin se gatea adentro (orgAdminProcedure) */}
                  <Route path="/org" element={<OrgHomePage />} />
                  <Route path="/org/soluciones/:slug" element={<OrgSolutionPage />} />
                  <Route path="/org/personas/:email" element={<OrgPersonPage />} />
                  <Route path="/org/clientes/:clientId" element={<OrgClientPage />} />
                  <Route path="/org/admin" element={<OrgAdminPage />} />
                  <Route path="/org/api" element={<OrgApiDocsPage />} />

                  {/* People Landing — all authenticated users (People edita) */}
                  <Route path="/people/landing" element={<PeopleLandingPage />} />
                  <Route path="/people/landing/:slug" element={<PeopleLandingPage />} />
                  {/* People Learning — todos los clappers juegan; dashboard/editor
                      gateados por probe adentro (política audit-roles) */}
                  <Route path="/people/learning" element={<LearningPage />} />
                  {/* Winclap Studio — Programas (spec de Learning 19/10/2026): solo funciones del Studio */}
                  <Route element={<StudioProtectedRoute />}>
                    <Route path="/studio" element={<StudioPage />} />
                  </Route>

                  {/* Pricing Manager (Finance) — todos crean/ven; tutorial ANTES de :id */}
                  <Route path="/finance/pricing" element={<PricingSavedVersionsPage />} />
                  <Route path="/finance/pricing/tutorial" element={<PricingTutorialPage />} />
                  <Route path="/finance/pricing/:id" element={<PricingEditorPage />} />

                  {/* Finance → Control Presupuestario — acceso por governance FINANCE_PNL_GOVERNANCE
                      (Juan Cruz + admins; viewers por persona o team). Misma audiencia que finance-pnl.ts. */}
                  <Route element={<FinancePnlProtectedRoute />}>
                    <Route path="/finance/budget-control" element={<BudgetControlPage />} />
                  </Route>

                  {/* People → Talent Acquisition — acceso por governance TA_GOVERNANCE
                      (Ignacio Bresso + admins; viewers por persona o team). */}
                  <Route element={<TaProtectedRoute />}>
                    <Route path="/people/talent-acquisition" element={<TalentAcquisitionPage />} />
                    <Route path="/people/talent-acquisition/intake" element={<TaIntakePage />} />
                    <Route path="/people/talent-acquisition/interview" element={<TaInterviewPage />} />
                  </Route>

                  {/* Partnerships Hub — el acceso lo resuelve el governance
                      config-driven contra el community map. El gate va en la ruta:
                      sin él, quien no tiene acceso entra igual y ve el hub vacío
                      (FORBIDDEN silencioso, prohibido por la política de roles). */}
                  <Route element={<PartnershipsProtectedRoute />}>
                    <Route path="/partnerships" element={<PartnershipsOverviewPage />} />
                    <Route path="/partnerships/governance" element={<PartnershipsGovernancePage />} />
                    <Route path="/partnerships/:partner" element={<PartnerHubPage />} />
                    <Route path="/partnerships/:partner/products" element={<PartnerProductsPage />} />
                    <Route path="/partnerships/:partner/packages" element={<PartnerPackagesPage />} />
                    <Route path="/partnerships/:partner/cases" element={<PartnerCasesPage />} />
                    <Route path="/partnerships/:partner/faq" element={<PartnerFAQPage />} />
                    <Route path="/partnerships/:partner/products/:id" element={<ProductDetailPage />} />
                    <Route path="/partnerships/:partner/packages/:id" element={<PackageDetailPage />} />
                    <Route path="/partnerships/:partner/cases/:id" element={<CaseDetailPage />} />
                  </Route>

                  {/* Owned Media (OML) — apartado N3: el acceso lo resuelve el governance
                      OML_GOVERNANCE + asignaciones vigentes + grants. Gate en la ruta con
                      mensaje explícito de sin acceso (AC-4); nunca una página vacía. */}
                  <Route element={<OmlProtectedRoute />}>
                    <Route path="/owned-media" element={<OmlAccountsPage />} />
                    <Route path="/owned-media/home" element={<OmlHomePage />} />
                    <Route path="/owned-media/accounts/:accountId" element={<OmlAccountDetailPage />} />
                    <Route path="/owned-media/journeys" element={<OmlTransversalPage kind="journeys" />} />
                    <Route path="/owned-media/experiments" element={<OmlTransversalPage kind="experiments" />} />
                    <Route path="/owned-media/projects" element={<OmlTransversalPage kind="projects" />} />
                    <Route path="/owned-media/platforms" element={<OmlTransversalPage kind="platforms" />} />
                    <Route path="/owned-media/health" element={<OmlHealthPage />} />
                    <Route path="/owned-media/archived" element={<OmlArchivedPage />} />
                    <Route path="/owned-media/admin" element={<OmlAdminPage />} />
                  </Route>

                  {/* Vault — la ruta se abre a todo autenticado; el gate (2SV de
                      Google / grants / passphrase) lo hace VaultProtectedRoute con
                      pantallas explícitas, nunca una página vacía. Se sirve desde
                      otra Lambda (splitLink en lib/trpc.ts). */}
                  <Route element={<VaultProtectedRoute />}>
                    <Route path="/vault" element={<VaultPage />} />
                    <Route path="/vault/secrets/:secretId" element={<SecretDetailPage />} />
                    <Route path="/vault/groups" element={<GroupsPage />} />
                    <Route path="/vault/audit" element={<AuditPage />} />
                    {/* Censo (F6): admin Y el gate del Vault. vaultAdminProcedure exige
                        2SV; fuera de VaultProtectedRoute un FORBIDDEN se vería como
                        "censo vacío" (review F6). */}
                    <Route element={<ProtectedRoute authHook={useAuth} requiredRoles={['admin']} />}>
                      <Route path="/vault/shared-accounts" element={<SharedAccountsPage />} />
                    </Route>
                  </Route>
                  {/* Censo (F6): el dueño propuesto responde el formulario. Solo login:
                      puede no tener acceso al Vault ni passphrase (self-scoped en el backend). */}
                  <Route path="/vault/census" element={<CensusFormPage />} />
                  <Route path="/vault/census/:accountEmail" element={<CensusFormPage />} />
                  {/* Workshop guiado del Vault (25/09): solo login, FUERA del gate — es la
                      forma de conocer el Vault antes de tener accesos, 2SV o passphrase.
                      Corre sobre datos de demo en memoria (lib/vault-demo). */}
                  <Route path="/vault/workshop" element={<WorkshopPage />} />
                  <Route path="/skills-agentes/new" element={<NewSkillAgentPage />} />
                  <Route path="/skills-agentes/:skillId/new-version" element={<NewSkillAgentPage />} />
                  <Route path="/skills-agentes/:skillId/history" element={<SkillVersionHistoryPage />} />

                  {/* Tickets — all authenticated users (incl. clapper) */}
                  <Route path="/tickets/select" element={<SelectTicketTypePage />} />
                  <Route path="/tickets/new/:formType" element={<NewTicketPage />} />
                  <Route path="/tickets/mine" element={<MyTicketsPage />} />

                  {/* Admin Tickets — elevated roles only */}
                  <Route
                    element={
                      <ProtectedRoute
                        authHook={useAuth}
                        requiredRoles={['admin', 'tech', 'finance', 'people']}
                      />
                    }
                  >
                    <Route path="/admin/tickets" element={<AllTicketsPage />} />
                  </Route>

                  {/* User Permissions — admin only */}
                  <Route
                    element={
                      <ProtectedRoute authHook={useAuth} requiredRoles={['admin']} />
                    }
                  >
                    <Route path="/admin/users/permissions" element={<UserPermissionsPage />} />
                  </Route>

                  {/* Panel de Internal — admin, o permiso extra subtab:internal:adoption (solo pestañas Adopción y Brain) */}
                  <Route
                    element={
                      <ProtectedRoute authHook={useAuth} requiredRoles={['admin']} requiredExtraPermission="subtab:internal:adoption" />
                    }
                  >
                    <Route path="/admin/internal" element={<InternalPanelPage />} />
                  </Route>

                  {/* Inventory — tech + finance + admin, or tab:inventory extra perm */}
                  <Route
                    element={
                      <ProtectedRoute
                        authHook={useAuth}
                        requiredRoles={['tech', 'finance', 'people', 'admin']}
                        requiredExtraPermission={['tab:inventory', 'tab:approvals']}
                      />
                    }
                  >
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:inventory:main" />}>
                      <Route path="/admin/inventory" element={<InventoryPage />} />
                    </Route>
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:inventory:useful-info" />}>
                      <Route path="/admin/devices/useful-info" element={<UsefulInfoPage />} />
                    </Route>
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:inventory:approval" />}>
                      <Route path="/admin/devices/approval" element={<ApprovalForPurchasePage />} />
                    </Route>
                  </Route>

                  {/* Users / People — tech + finance + people + admin, or tab:users extra perm */}
                  <Route
                    element={
                      <ProtectedRoute
                        authHook={useAuth}
                        requiredRoles={['tech', 'finance', 'people', 'admin']}
                        requiredExtraPermission="tab:users"
                      />
                    }
                  >
                    <Route path="/admin/users" element={<UserManagementPage />} />
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:users:requests" />}>
                      <Route path="/admin/users/pedidos" element={<UserRequestsKanbanPage />} />
                    </Route>
                    {/* In/out: altas + bajas IT en una sola página (los permisos
                        por sub-tab los gatea la página); las rutas viejas redirigen */}
                    <Route path="/admin/devices/in-out" element={<InOutPage />} />
                    <Route
                      path="/admin/users/onboarding"
                      element={<Navigate to="/admin/devices/in-out?tab=altas" replace />}
                    />
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:users:people-panel" />}>
                      <Route path="/admin/users/people-panel" element={<PeoplePanelPage />} />
                    </Route>
                    <Route
                      path="/admin/users/offboarding"
                      element={<Navigate to="/admin/devices/in-out?tab=bajas" replace />}
                    />
                    <Route
                      path="/admin/users/inactive"
                      element={<Navigate to="/admin/devices/in-out?tab=inactivos" replace />}
                    />
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:users:community-map" />}>
                      <Route path="/admin/users/community-map" element={<CommunityMapPage />} />
                    </Route>
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:users:oficinas" />}>
                      <Route path="/admin/users/oficinas" element={<OfficesPage />} />
                    </Route>
                  </Route>

                  {/* Office Manager — acceso config-driven (governance), fuera del
                      bloque de users para que una OM sin roles ni tab:users entre igual */}
                  <Route element={<OfficeManagerProtectedRoute />}>
                    <Route path="/admin/users/office-manager" element={<OfficeManagerPage />} />
                  </Route>

                  {/* IA Native — Dashboard y Clappers dados de baja (07/08/2026);
                      quedan solo Subscriptions y Observability */}

                  {/* AI & Subscriptions — growth + finance + admin, or tab:ia-native extra perm */}
                  <Route
                    element={
                      <ProtectedRoute
                        authHook={useAuth}
                        requiredRoles={['growth', 'finance', 'admin']}
                        requiredExtraPermission="tab:ia-native"
                      />
                    }
                  >
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:ia-native:subscriptions" />}>
                      <Route path="/ia-native/subscriptions" element={<SubscriptionsIntelligencePage />} />
                    </Route>
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:ia-native:observability" />}>
                      <Route path="/ia-native/observability" element={<ObservabilityPage />} />
                    </Route>
                  </Route>

                  {/* Technology → FinOps — tech + finance + admin, o permiso extra tab:finops */}
                  <Route
                    element={
                      <ProtectedRoute
                        authHook={useAuth}
                        requiredRoles={['tech', 'finance', 'admin']}
                        requiredExtraPermission="tab:finops"
                      />
                    }
                  >
                    <Route path="/technology/finops" element={<FinOpsPage />} />
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab={FINOPS_ALERTS_SUBTAB} />}>
                      <Route path="/technology/finops/alerts" element={<FinOpsAlertsPage />} />
                    </Route>
                  </Route>

                  {/* Performance — people + admin, or users with tab:performance extra perm */}
                  <Route
                    element={
                      <ProtectedRoute
                        authHook={useAuth}
                        requiredRoles={['people', 'admin']}
                        requiredExtraPermission="tab:performance"
                      />
                    }
                  >
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:performance:evaluations" />}>
                      {/* Hub "Evaluaciones" oculto (Sol 02/09): redirect; PerformanceStart queda para reactivar */}
                      <Route path="/performance" element={<Navigate to="/performance/dtalk" replace />} />
                      <Route path="/performance/epic/:feedbackId" element={<PerformanceEvaluation />} />
                      <Route path="/performance/epic/:feedbackId/analysis" element={<PerformanceAnalysis />} />
                      <Route path="/performance/epic/:feedbackId/feedback-result" element={<PerformanceFeedbackResult />} />
                      <Route path="/performance/epic/:feedbackId/performance-review" element={<PerformanceReview />} />
                      <Route path="/performance/clapper/:email" element={<PerformanceClapperProfile />} />
                    </Route>
                    <Route element={<ProtectedRoute authHook={useAuth} deniedIfSubTab="subtab:performance:history" />}>
                      <Route path="/performance/history" element={<PerformanceHistory />} />
                    </Route>
                  </Route>

                  {/* Performance Admin — admin, or users with subtab:performance:admin extra perm */}
                  <Route
                    element={
                      <ProtectedRoute authHook={useAuth} requiredRoles={['admin']} requiredExtraPermission="subtab:performance:admin" />
                    }
                  >
                    <Route path="/performance/admin" element={<PerformanceAdminLayout />}>
                      <Route index element={<PerformanceAdminDashboard />} />
                      <Route path="epics" element={<PerformanceAdminEpics />} />
                      <Route path="assistants" element={<PerformanceAdminAssistants />} />
                      <Route path="knowledge" element={<PerformanceAdminKnowledge />} />
                      <Route path="notifications" element={<PerformanceAdminNotifications />} />
                      <Route path="governance" element={<TalentMapGovernancePage />} />
                    </Route>
                  </Route>

                  {/* D-Talk — cada clapper llega a SU conversación (backend abierto via
                      privateProcedure); las tabs de equipo/people/histórico se gatean
                      por acceso resuelto adentro del layout. Así las vistas coinciden
                      con el rol real: clapper → su D-Talk, líder → su equipo,
                      People/HRBP → vista People. */}
                  <Route path="/performance/dtalk" element={<DTalkLayout />}>
                    <Route index element={<PerformanceDTalk />} />
                    <Route path="team" element={<DTalkPeopleTab scope="team" />} />
                    <Route path="team/:email" element={<DTalkDetail />} />
                    <Route path="people" element={<DTalkPeopleTab />} />
                    <Route path="people/:email" element={<DTalkDetail />} />
                    <Route path="history" element={<DTalkHistoryTab />} />
                    <Route path="history/:email" element={<DTalkHistoryTab />} />
                  </Route>

                  {/* Mapa de Talento — acceso resuelto por governance (líder / people / hrbp / admin) */}
                  <Route element={<TalentMapProtectedRoute />}>
                    <Route path="/performance/talent-map" element={<TalentMapLayout />}>
                      <Route index element={<Navigate to="team" replace />} />
                      <Route path="team" element={<TalentMapTeamTab />} />
                      <Route path="evaluation" element={<TalentMapEvaluationTab />} />
                      <Route path="evaluation/:email" element={<TalentMapEvaluationTab />} />
                      <Route path="history" element={<TalentMapHistoryTab />} />
                      <Route path="history/:email" element={<TalentMapHistoryTab />} />
                      <Route path="analytics" element={<TalentMapAnalyticsTab />} />
                      <Route path="admin" element={<Navigate to="/performance/talent-map/team" replace />} />
                      <Route path=":email" element={<LegacyTalentMapRedirect />} />
                    </Route>
                  </Route>
                </Route>
              </Route>

              {/* Redirect root to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 404 */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>

            <Toaster />
          </PageContextProvider>
          </AuthProvider>
        </BrowserRouter>

        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </trpc.Provider>
    </ThemeProvider>
  );
}

export default App;
